import React, { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
  CircularProgress,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { BASE_URL } from "../../../services/configUrls";

const PaymentApprovalTable = ({ type = "all" }) => {
  const queryClient = useQueryClient();

  // type can be "fdp", "tech_camp" / "techcamp", or "all"
  const resolvedEventType =
    type === "techcamp" || type === "tech_camp"
      ? "tech_camp"
      : type === "fdp"
      ? "fdp"
      : null;

  /* Pagination (MUI page is 0-indexed, backend page is 1-indexed) */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* Accept Dialog */
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [selectedRowForAccept, setSelectedRowForAccept] = useState(null);

  /* Reject Dialog */
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRowForReject, setSelectedRowForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState("");

  /* Toast Notification */
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  /* ---------------- REACT QUERY FETCH DATA ---------------- */
  const {
    data: responseData,
    isLoading: loading,
  } = useQuery(
    ["all-institute-interests-status1", resolvedEventType, page, rowsPerPage],
    async () => {
      const res = await axios.get(
        `${BASE_URL}/event/all-institute-interests-status1`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            ...(resolvedEventType ? { event_type: resolvedEventType } : {}),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 1000 * 60, // 1 minute
    }
  );

  // Handle both array response and paginated object response { data: [...], total_records: number }
  const list = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.data)
    ? responseData.data
    : [];

  const totalCount = responseData?.total_records ?? list.length;

  /* ---------------- MUTATIONS ---------------- */
  const acceptMutation = useMutation(
    async ({ interest_id }) => {
      const res = await axios.put(
        `${BASE_URL}/event/activity-interest/status-update`,
        { interest_id, status: 4 },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return res.data;
    },
    {
      onSuccess: () => {
        showToast("Payment approved successfully!");
        closeAcceptDialog();
        queryClient.invalidateQueries(["all-institute-interests-status1"]);
      },
      onError: (err) => {
        showToast(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            "Failed to approve payment.",
          "error"
        );
      },
    }
  );

  const rejectMutation = useMutation(
    async ({ interest_id, reason }) => {
      const res = await axios.put(
        `${BASE_URL}/event/activity-interest/status-update`,
        {
          interest_id,
          status: 2,
          reason,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return res.data;
    },
    {
      onSuccess: () => {
        showToast("Payment request rejected/cancelled.");
        closeRejectDialog();
        queryClient.invalidateQueries(["all-institute-interests-status1"]);
      },
      onError: (err) => {
        setRejectError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            "Failed to reject payment request."
        );
      },
    }
  );

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—";

  /* ---------------- ACCEPT ACTION ---------------- */
  const openAcceptDialog = (row) => {
    setSelectedRowForAccept(row);
    setAcceptDialogOpen(true);
  };

  const closeAcceptDialog = () => {
    setAcceptDialogOpen(false);
    setSelectedRowForAccept(null);
  };

  const handleConfirmAccept = () => {
    if (!selectedRowForAccept) return;
    acceptMutation.mutate({
      interest_id: selectedRowForAccept.interest_id,
    });
  };

  /* ---------------- REJECT ACTION ---------------- */
  const openRejectDialog = (row) => {
    setSelectedRowForReject(row);
    setRejectReason("");
    setRejectError("");
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedRowForReject(null);
    setRejectReason("");
    setRejectError("");
  };

  const handleConfirmReject = () => {
    if (!selectedRowForReject) return;
    if (!rejectReason.trim()) {
      setRejectError("Reason is required.");
      return;
    }

    rejectMutation.mutate({
      interest_id: selectedRowForReject.interest_id,
      reason: rejectReason.trim(),
    });
  };

  const actionLoading = acceptMutation.isLoading || rejectMutation.isLoading;

  return (
    <Box>
      {/* ---------------- TABLE ---------------- */}
      <Paper
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell>
                    <b>Institute</b>
                  </TableCell>
                  <TableCell>
                    <b>Domain</b>
                  </TableCell>
                  <TableCell>
                    <b>Type</b>
                  </TableCell>
                  <TableCell>
                    <b>Date Range</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {list.map((row) => (
                  <TableRow key={row.interest_id} hover>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                      {row.institute_name}
                    </TableCell>
                    <TableCell sx={{ color: "#475569" }}>
                      {row.domain_name}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          row.event_type?.toLowerCase() === "tech_camp" ||
                          row.event_type?.toLowerCase() === "techcamp"
                            ? "Tech Camp"
                            : "FDP"
                        }
                        color={
                          row.event_type?.toLowerCase() === "tech_camp" ||
                          row.event_type?.toLowerCase() === "techcamp"
                            ? "secondary"
                            : "primary"
                        }
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                      {formatDate(row.start_date)} – {formatDate(row.end_date)}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                          onClick={() => openAcceptDialog(row)}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            fontWeight: 600,
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 1.5,
                            boxShadow: "none",
                          }}
                        >
                          Accept
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CancelOutlinedIcon fontSize="small" />}
                          onClick={() => openRejectDialog(row)}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            fontWeight: 600,
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 1.5,
                          }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary" fontWeight={500}>
                        No pending payment approval requests found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* ---------------- ACCEPT DIALOG ---------------- */}
      <Dialog
        open={acceptDialogOpen}
        onClose={closeAcceptDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Approve Payment</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to approve the payment verification for:
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography fontSize={14} fontWeight={600} color="#1e293b">
              {selectedRowForAccept?.institute_name}
            </Typography>
            <Typography fontSize={13} color="#64748b" sx={{ mt: 0.5 }}>
              Domain: <b>{selectedRowForAccept?.domain_name}</b>
            </Typography>
            <Typography fontSize={12} color="#64748b" sx={{ mt: 0.5 }}>
              Duration: {formatDate(selectedRowForAccept?.start_date)} to{" "}
              {formatDate(selectedRowForAccept?.end_date)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeAcceptDialog}
            disabled={actionLoading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmAccept}
            disabled={actionLoading}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {actionLoading ? "Approving..." : "Confirm Approval"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- REJECT DIALOG ---------------- */}
      <Dialog
        open={rejectDialogOpen}
        onClose={closeRejectDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Reject Payment Request</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for rejecting the payment for{" "}
            <b>{selectedRowForReject?.institute_name}</b> (
            {selectedRowForReject?.domain_name}):
          </Typography>
          <TextField
            label="Reason *"
            multiline
            rows={3}
            fullWidth
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              if (e.target.value.trim()) setRejectError("");
            }}
            error={!!rejectError}
            helperText={rejectError}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeRejectDialog}
            disabled={actionLoading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={actionLoading}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {actionLoading ? "Rejecting..." : "Confirm Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- TOAST NOTIFICATION ---------------- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentApprovalTable;
